import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldAlert, Trash2, Download, Cookie, HeartPulse, Mail, FileCheck2,
  Check, X, Loader2, User as UserIcon, ShieldCheck, FileJson, FileSpreadsheet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

/* ============================ Consent Dashboard ============================ */
const CONSENT_META = {
  cookie: { icon: Cookie, title: "Cookie Consent",
    desc: "We use cookies for site functionality and analytics." },
  health_data: { icon: HeartPulse, title: "Health Data Consent",
    desc: "Allows us to collect and process your medical history, diagnoses, treatment preferences, and allergies for personalized hospital matching." },
  marketing: { icon: Mail, title: "Marketing Consent",
    desc: "Receive promotional emails about health packages, wellness retreats, and special offers from our partner hospitals." },
};

function ConsentCard({ type, state, onGrant, onWithdraw, busy }) {
  const meta = CONSENT_META[type];
  const Icon = meta.icon;
  const granted = state?.granted;
  return (
    <div className="rounded-2xl border border-[#E4E7EC] bg-white p-5 flex flex-col" data-testid={`consent-card-${type}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="h-10 w-10 rounded-xl grid place-items-center bg-[#0D5C75]/10 text-[#0D5C75]"><Icon size={20} /></span>
          <h3 className="font-heading font-bold text-[#101828]">{meta.title}</h3>
        </div>
        {granted
          ? <Badge className="bg-[#D1FAE5] text-[#087443] border-0" data-testid={`consent-status-${type}`}><Check size={12} className="mr-1" />Granted</Badge>
          : <Badge className="bg-gray-100 text-gray-500 border-0" data-testid={`consent-status-${type}`}><X size={12} className="mr-1" />Not granted</Badge>}
      </div>
      <p className="text-sm text-muted-foreground mt-3 flex-1">{meta.desc}</p>
      <div className="text-xs text-muted-foreground mt-3">
        {granted ? <>Granted on <span className="font-medium text-[#475467]">{fmtDate(state?.granted_at)}</span></> : "Not currently granted"}
      </div>
      <div className="mt-4">
        {granted ? (
          <Button variant="outline" size="sm" disabled={busy} onClick={() => onWithdraw(type)} data-testid={`consent-action-${type}`}
            className="rounded-full border-[#B42318]/40 text-[#B42318] hover:bg-[#FEE4E2] hover:text-[#B42318]">Withdraw</Button>
        ) : (
          <Button size="sm" disabled={busy} onClick={() => onGrant(type)} data-testid={`consent-action-${type}`}
            className="rounded-full bg-[#0D5C75] text-white hover:bg-[#094A5E] hover:text-white">Grant</Button>
        )}
      </div>
    </div>
  );
}

function ConsentDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [healthModal, setHealthModal] = useState(false);

  const load = () => api.get("/v1/users/me/consents").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);

  const grant = async (type) => {
    setBusy(true);
    try { await api.post("/v1/users/me/consents/grant", { consent_type: type }); toast.success("Consent granted"); await load(); }
    catch { toast.error("Could not update consent"); } finally { setBusy(false); }
  };

  const doWithdraw = async (type) => {
    setBusy(true);
    try {
      await api.post("/v1/users/me/consents/withdraw", { consent_type: type });
      if (type === "cookie") { try { localStorage.removeItem("ss_cookie_consent"); } catch { /* noop */ } }
      toast.success("Consent withdrawn");
      await load();
    } catch { toast.error("Could not update consent"); } finally { setBusy(false); setHealthModal(false); }
  };

  const withdraw = (type) => { if (type === "health_data") setHealthModal(true); else doWithdraw(type); };

  if (!data) return <div className="py-16 grid place-items-center text-muted-foreground"><Loader2 className="animate-spin" /></div>;

  const c = data.consents;
  return (
    <div data-testid="consent-dashboard">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ConsentCard type="cookie" state={c.cookie} onGrant={grant} onWithdraw={withdraw} busy={busy} />
        <ConsentCard type="health_data" state={c.health_data} onGrant={grant} onWithdraw={withdraw} busy={busy} />
        <ConsentCard type="marketing" state={c.marketing} onGrant={grant} onWithdraw={withdraw} busy={busy} />
        {/* Terms & Privacy */}
        <div className="rounded-2xl border border-[#E4E7EC] bg-white p-5 flex flex-col" data-testid="consent-card-terms">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <span className="h-10 w-10 rounded-xl grid place-items-center bg-[#0D5C75]/10 text-[#0D5C75]"><FileCheck2 size={20} /></span>
              <h3 className="font-heading font-bold text-[#101828]">Terms & Privacy Policy</h3>
            </div>
            <Badge className="bg-[#D1FAE5] text-[#087443] border-0"><Check size={12} className="mr-1" />Accepted</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-3 flex-1">You accepted our{" "}
            <button className="text-[#0D5C75] underline" onClick={() => navigate("/terms")}>Terms of Service</button> and{" "}
            <button className="text-[#0D5C75] underline" onClick={() => navigate("/privacy")}>Privacy Policy</button> during registration.</p>
          <div className="text-xs text-muted-foreground mt-3">Accepted on <span className="font-medium text-[#475467]">{fmtDate(c.terms?.accepted_at || c.privacy?.accepted_at)}</span></div>
          <div className="mt-4 text-xs text-muted-foreground italic">Required for registration — cannot be withdrawn.</div>
        </div>
      </div>

      {/* History */}
      <div className="mt-8">
        <h3 className="font-heading font-bold text-[#101828] mb-3">Consent History</h3>
        <div className="rounded-2xl border border-[#E4E7EC] bg-white overflow-x-auto" data-testid="consent-history">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Action</TableHead><TableHead>Type</TableHead><TableHead>Details</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.history.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">No consent history yet.</TableCell></TableRow>}
              {data.history.map((h, i) => (
                <TableRow key={i} data-testid="consent-history-row">
                  <TableCell className="text-xs">{fmtDate(h.timestamp)}</TableCell>
                  <TableCell><span className={`font-semibold ${h.granted ? "text-[#087443]" : "text-[#B42318]"}`}>{h.action}</span></TableCell>
                  <TableCell className="capitalize">{(h.consent_type || "").replace("_", " ")}</TableCell>
                  <TableCell className="text-muted-foreground capitalize">{h.level}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={healthModal} onOpenChange={setHealthModal}>
        <DialogContent className="max-w-md" data-testid="health-withdraw-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#B42318]"><ShieldAlert size={20} /> Withdraw health data consent?</DialogTitle>
            <DialogDescription>This affects Booking and Matchmaker access.</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-[#475467]">Withdrawing health data consent will prevent you from using the Booking and Matchmaker features. Your existing health data will be retained but no longer processed. Are you sure?</p>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setHealthModal(false)} className="rounded-full" data-testid="health-withdraw-cancel">Cancel</Button>
            <Button onClick={() => doWithdraw("health_data")} disabled={busy} data-testid="health-withdraw-confirm"
              className="rounded-full bg-[#B42318] text-white hover:bg-[#912018] hover:text-white">{busy ? "Withdrawing…" : "Withdraw consent"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============================ Data Export ============================ */
function DataExport() {
  const [format, setFormat] = useState("json");
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("mv_token") || "";
      const res = await fetch(`${API}/v1/users/me/export?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 429) { toast.error("Export limit reached (3 per hour). Please try again later."); return; }
      if (!res.ok) { toast.error("Could not generate export."); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.download = `sarvaseva-data-${stamp}.${format === "csv" ? "zip" : "json"}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Your data export has been downloaded");
    } catch { toast.error("Could not generate export."); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-[#E4E7EC] bg-white p-6" data-testid="data-export">
      <div className="flex items-center gap-2 text-[#0D5C75]"><Download size={18} /><h3 className="font-heading font-bold">Download Your Data</h3></div>
      <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
        You have the right to receive a copy of your personal data (GDPR Article 20). Click below to download a file containing all the data we hold about you.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-full border border-[#E4E7EC] bg-[#F7F9FC] p-1" data-testid="export-format-toggle">
          <button onClick={() => setFormat("json")} data-testid="export-format-json"
            className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 ${format === "json" ? "bg-white shadow-sm text-[#0D5C75]" : "text-[#475467]"}`}><FileJson size={15} /> JSON</button>
          <button onClick={() => setFormat("csv")} data-testid="export-format-csv"
            className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 ${format === "csv" ? "bg-white shadow-sm text-[#0D5C75]" : "text-[#475467]"}`}><FileSpreadsheet size={15} /> CSV (ZIP)</button>
        </div>
        <Button onClick={download} disabled={busy} data-testid="download-data-btn"
          className="rounded-full bg-[#0D5C75] text-white hover:bg-[#094A5E] hover:text-white">
          {busy ? <><Loader2 size={16} className="mr-1.5 animate-spin" /> Preparing…</> : <><Download size={16} className="mr-1.5" /> Download My Data</>}
        </Button>
      </div>
    </div>
  );
}

/* ============================ Delete Account ============================ */
function DeleteAccount() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const requestDeletion = async () => {
    setBusy(true);
    try {
      await api.delete("/users/me");
      toast.success("Your personal data has been anonymized.");
      setOpen(false); logout(); navigate("/");
    } catch { toast.error("Could not process your request. Please try again."); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-[#FDA29B]/50 bg-[#FFFBFA] p-6" data-testid="account-settings">
      <div className="flex items-center gap-2 text-[#B42318]"><ShieldAlert size={18} /><h3 className="font-heading font-bold">Delete Your Account</h3></div>
      <p className="text-sm text-[#B42318]/80 mt-2 font-medium" data-testid="delete-export-hint">Before deleting, you may want to download a copy of your data above.</p>
      <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
        Under GDPR and the DPDP Act you can request erasure of your personal data at any time. We will permanently
        anonymize your identifying information (name, email, health profile). Anonymized financial and booking records
        are retained to meet legal and accounting obligations.
      </p>
      <Button variant="outline" onClick={() => { setConfirm(""); setOpen(true); }} data-testid="request-deletion-btn"
        className="mt-4 rounded-full border-[#B42318]/40 text-[#B42318] hover:bg-[#FEE4E2] hover:text-[#B42318]">
        <Trash2 size={16} className="mr-1.5" /> Request data deletion
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="deletion-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#B42318]"><ShieldAlert size={20} /> Confirm data deletion</DialogTitle>
            <DialogDescription>Permanently anonymize your account and health profile. This cannot be undone.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm text-[#475467]">
            <p>This will permanently anonymize your account and health profile. This action cannot be undone and you will be signed out.</p>
            <p>Type <span className="font-mono font-semibold text-[#101828]">DELETE</span> to confirm.</p>
            <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="DELETE" data-testid="deletion-confirm-input" />
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full border-[#E4E7EC]" data-testid="deletion-cancel-btn">Cancel</Button>
            <Button onClick={requestDeletion} disabled={confirm !== "DELETE" || busy} data-testid="deletion-confirm-btn"
              className="rounded-full bg-[#B42318] text-white hover:bg-[#912018] hover:text-white">{busy ? "Processing…" : "Delete my data"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============================ Profile ============================ */
function ProfileTab() {
  const { user } = useAuth();
  const rows = [
    { label: "Name", value: user?.name },
    { label: "Email", value: user?.email },
    { label: "Phone", value: user?.phone || "Not provided" },
    { label: "Country", value: user?.country || "Not provided" },
    { label: "Member since", value: user?.created_at ? fmtDate(user.created_at) : "—" },
  ];
  return (
    <div className="rounded-2xl border border-[#E4E7EC] bg-white p-6" data-testid="profile-tab">
      <div className="flex items-center gap-2 text-[#0D5C75] mb-4"><UserIcon size={18} /><h3 className="font-heading font-bold">Account Profile</h3></div>
      <dl className="divide-y divide-[#F1F5F9]">
        {rows.map((r) => (
          <div key={r.label} className="flex justify-between py-3 text-sm">
            <dt className="text-muted-foreground">{r.label}</dt>
            <dd className="font-medium text-[#101828]">{r.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/* ============================ Tabbed shell ============================ */
export default function AccountSettings() {
  return (
    <div className="mt-12" data-testid="account-settings-page">
      <div className="flex items-center gap-2 mb-5">
        <ShieldCheck size={20} className="text-[#0D5C75]" />
        <h2 className="font-heading text-xl font-bold text-[#101828]">Account Settings</h2>
      </div>
      <Tabs defaultValue="profile">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="profile" data-testid="account-tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="consents" data-testid="account-tab-consents">Privacy & Consents</TabsTrigger>
          <TabsTrigger value="data" data-testid="account-tab-data">Your Data</TabsTrigger>
        </TabsList>
        <TabsContent value="profile" className="mt-5"><ProfileTab /></TabsContent>
        <TabsContent value="consents" className="mt-5"><ConsentDashboard /></TabsContent>
        <TabsContent value="data" className="mt-5 space-y-6">
          <DataExport />
          <DeleteAccount />
        </TabsContent>
      </Tabs>
    </div>
  );
}
